// Viewport-pinned table header while the table scrolls horizontally inside
// an overflow-x container. Native position:sticky cannot do both: any
// non-visible overflow on an ancestor becomes the sticky containing block,
// and wide tables grow with that ancestor instead of scrolling the page, so
// the header never pins under the app bar. floatThead used to solve this
// with jQuery; this is the same idea without the dependency — a fixed clone
// of <thead> synced to column widths and the container's scrollLeft.

const FLOAT_CLASS = 'sticky-header-float';

const readHeaderOffsetPx = () => {
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue('--header-height')
    .trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 64;
};

const syncColumnWidths = (sourceHead, targetHead) => {
  const sourceCells = sourceHead.querySelectorAll('th');
  const targetCells = targetHead.querySelectorAll('th');
  sourceCells.forEach((cell, index) => {
    const target = targetCells[index];
    if (!target) {
      return;
    }
    const width = `${cell.getBoundingClientRect().width}px`;
    target.style.width = width;
    target.style.minWidth = width;
    target.style.maxWidth = width;
    target.style.boxSizing = 'border-box';
  });
};

const interactiveIn = (cell: Element) => {
  return (
    cell.querySelector('.MuiTableSortLabel-root') ||
    cell.querySelector('button, [role="button"], a, input, label')
  );
};

export const setupStickyHeader = (container, table) => {
  if (!container || !table?.tHead) {
    return () => {};
  }

  const thead = table.tHead;
  let floatRoot: HTMLDivElement | null = null;
  let floatTable: HTMLTableElement | null = null;
  let floatHead: HTMLTableSectionElement | null = null;
  let rafId = null;

  const destroyFloat = () => {
    if (floatRoot) {
      floatRoot.remove();
    }
    floatRoot = null;
    floatTable = null;
    floatHead = null;
  };

  const syncFloatHeadContent = () => {
    if (!floatTable || !floatHead) {
      return;
    }
    const next = thead.cloneNode(true) as HTMLTableSectionElement;
    floatTable.replaceChild(next, floatHead);
    floatHead = next;
    syncColumnWidths(thead, floatHead);
  };

  const forwardFloatClick = (event: MouseEvent) => {
    if (!floatHead) {
      return;
    }
    const target = event.target as Element | null;
    const floatTh = target?.closest?.('th');
    if (!floatTh || !floatHead.contains(floatTh)) {
      return;
    }
    const index = Array.from(floatHead.querySelectorAll('th')).indexOf(floatTh);
    if (index < 0) {
      return;
    }
    const sourceTh = thead.querySelectorAll('th')[index];
    if (!sourceTh) {
      return;
    }
    // cloneNode drops React listeners — activate the live header cell instead.
    const interactive = interactiveIn(sourceTh);
    (interactive || sourceTh).click();
  };

  const ensureFloat = () => {
    if (floatRoot) {
      return;
    }
    floatRoot = document.createElement('div');
    floatRoot.className = FLOAT_CLASS;
    floatRoot.setAttribute('aria-hidden', 'true');
    floatRoot.style.cssText = [
      'position:fixed',
      'z-index:3',
      'overflow:hidden',
      // Clicks are forwarded to the live <thead> (cloneNode has no React
      // handlers). pointer-events must be auto or sort labels are dead while
      // pinned.
      'pointer-events:auto',
      'display:none',
      'background:#f7f8f8',
      'box-shadow:0 1px 0 rgba(0,0,0,0.12)',
    ].join(';');

    floatTable = table.cloneNode(false) as HTMLTableElement;
    floatTable.className = table.className;
    floatTable.style.cssText =
      'border-collapse:collapse;table-layout:fixed;margin:0';
    floatHead = thead.cloneNode(true) as HTMLTableSectionElement;
    floatTable.appendChild(floatHead);
    floatRoot.appendChild(floatTable);
    floatRoot.addEventListener('click', forwardFloatClick);
    document.body.appendChild(floatRoot);
  };

  const update = () => {
    rafId = null;
    const topOffset = readHeaderOffsetPx();
    const tableRect = table.getBoundingClientRect();
    const headRect = thead.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const headHeight = headRect.height;
    const shouldStick =
      tableRect.top < topOffset && tableRect.bottom - headHeight > topOffset;

    if (!shouldStick) {
      destroyFloat();
      return;
    }

    ensureFloat();
    syncColumnWidths(thead, floatHead);

    const visibleWidth = container.clientWidth;
    floatRoot!.style.display = 'block';
    floatRoot!.style.top = `${topOffset}px`;
    floatRoot!.style.left = `${containerRect.left}px`;
    floatRoot!.style.width = `${visibleWidth}px`;
    floatRoot!.style.height = `${headHeight}px`;

    floatTable!.style.width = `${table.scrollWidth}px`;
    floatTable!.style.transform = `translateX(${-container.scrollLeft}px)`;
  };

  const scheduleUpdate = () => {
    if (rafId != null) {
      return;
    }
    rafId = window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate);
  container.addEventListener('scroll', scheduleUpdate, { passive: true });

  // Column content / font load can change widths after first paint.
  const resizeObserver =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(scheduleUpdate);
  resizeObserver?.observe(table);
  resizeObserver?.observe(container);

  // Sort labels / header text live in React's thead — refresh the clone when
  // that markup changes while pinned (e.g. asc/desc arrow).
  const mutationObserver =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver(() => {
          if (floatHead) {
            syncFloatHeadContent();
          }
        });
  mutationObserver?.observe(thead, {
    attributes: true,
    childList: true,
    subtree: true,
    characterData: true,
  });

  scheduleUpdate();

  return () => {
    if (rafId != null) {
      window.cancelAnimationFrame(rafId);
    }
    window.removeEventListener('scroll', scheduleUpdate);
    window.removeEventListener('resize', scheduleUpdate);
    container.removeEventListener('scroll', scheduleUpdate);
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    destroyFloat();
  };
};
